/**
 * ChecklistsTab - Lists checklist templates with quick actions.
 *
 * Split into one sub-tab per template kind — Work Orders and Inspections —
 * because the two are authored and used by different people for different
 * jobs, and a single mixed list gave no way to answer "what can a work order
 * pick from?". The kind is a server-side filter (?type=), so each sub-tab
 * fetches only its own rows, and the "New" button carries the active kind
 * into the editor so the author never re-picks what they just clicked.
 *
 * Renders a table of checklist templates that the Super Admin can
 * inspect, duplicate, edit, or delete. The actual editor lives in a
 * sibling component (ChecklistEditor) — this tab just owns the list.
 *
 * Action behaviors (calls server endpoints):
 *   - Edit → swaps the page over to the editor with the given id.
 *           When the editor saves, the server creates a NEW version
 *           and inactivates the prior one — handled server-side.
 *   - Duplicate → POSTs /checklists/{id}/duplicate which creates a
 *           fresh v1 with cleared visibility. The duplicate appears
 *           in the list on next refresh.
 *   - Delete → soft-deletes via DELETE /checklists/{id}. Confirmed
 *           with a window.confirm dialog (intentionally simple — a
 *           proper ConfirmModal can be wired in later).
 *   - New Checklist → enters editor in 'new' mode.
 *
 * The list re-fetches whenever the component first mounts and after
 * any mutation that changes the result set.
 */
import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Alert } from '../../components/atoms';
import { TabBar } from '../../components/molecules';
import { camelizeKeys } from '../../services/api';
import {
    checklistTemplateService,
    ChecklistTemplate,
    ChecklistTemplateType,
} from '../../services/checklistTemplateService';
import { qk } from '../../lib/queryKeys';

interface ChecklistsTabProps {
    /** Called with a template id when the user clicks "Edit" on a row */
    onEdit: (id: string) => void;
    /** Called with the active sub-tab's kind when the user clicks "New" */
    onCreate: (type: ChecklistTemplateType) => void;
    /**
     * The active sub-tab and the search text, owned by the CMS page. This
     * list is unmounted whenever the editor takes over the page, so state
     * kept in here reset to Work Order Templates — and an empty search —
     * every time someone saved or cancelled an inspection template.
     */
    type: ChecklistTemplateType;
    onTypeChange: (type: ChecklistTemplateType) => void;
    search: string;
    onSearchChange: (search: string) => void;
}

/* Sub-tab strip — one per template kind. Keys are the exact `type` values the
   API filters on, so the active key doubles as the query param. */
const TYPE_TABS: { key: ChecklistTemplateType; label: string }[] = [
    { key: 'work_order', label: 'Work Order Templates' },
    { key: 'inspection', label: 'Inspection Templates' },
];

/* Per-kind wording for the create button and the empty state. Keeping the
   copy beside the tab list means adding a third kind later is one entry,
   not a hunt through the JSX for every "checklist" that needs renaming. */
const TYPE_COPY: Record<ChecklistTemplateType, { noun: string; createLabel: string }> = {
    work_order: { noun: 'work order template', createLabel: '+ New Work Order Template' },
    inspection: { noun: 'inspection template', createLabel: '+ New Inspection Template' },
};

const ChecklistsTab: React.FC<ChecklistsTabProps> = ({ onEdit, onCreate, type, onTypeChange, search, onSearchChange }) => {
    const queryClient = useQueryClient();

    /* The active template kind and the search text come in as props — see
       ChecklistsTabProps. The search filters by title server-side. */

    /* List query — re-keys (and refetches) whenever `search` or `type` changes.
       The kind is part of the key so each sub-tab caches independently and
       switching back to one already viewed is instant. */
    const trimmedSearch = search.trim();
    const listQuery = useQuery({
        queryKey: qk.checklistTemplates.list({ search: trimmedSearch, type }),
        queryFn: async () => {
            const params: Record<string, string> = { type };
            if (trimmedSearch) {
                params.search = trimmedSearch;
            }
            const res = await checklistTemplateService.list(params);
            // The list endpoint returns Laravel pagination in snake_case.
            // api.ts does NOT camelize responses, so do it locally before
            // reading camelCase fields like itemsCount/isVisibleToAll/updatedAt.
            const rawRows = Array.isArray(res.data) ? res.data : ((res.data as any)?.data ?? []);
            return camelizeKeys(rawRows) as ChecklistTemplate[];
        },
    });

    const checklists: ChecklistTemplate[] = useMemo(() => listQuery.data ?? [], [listQuery.data]);
    const isLoading = listQuery.isLoading;
    const error = listQuery.error ? (listQuery.error as Error).message : null;

    /* ---------------------------------------------------------------
     * Action handlers
     * --------------------------------------------------------------- */

    /** Duplicate creates a fresh v1 with cleared visibility on the server */
    const duplicateMutation = useMutation({
        mutationFn: (id: string) => checklistTemplateService.duplicate(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.checklistTemplates.all }),
    });
    const handleDuplicate = async (id: string) => {
        try {
            await duplicateMutation.mutateAsync(id); // Show the new copy in the list
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to duplicate');
        }
    };

    /** Soft-delete the template — server preserves snapshotted instances */
    const deleteMutation = useMutation({
        mutationFn: (id: string) => checklistTemplateService.delete(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.checklistTemplates.all }),
    });
    const handleDelete = async (id: string, title: string) => {
        // Intentionally simple confirmation — wire ConfirmModal in later if desired
        if (!window.confirm(`Delete template "${title}"? Existing assignments are unaffected.`)) {
            return;
        }
        try {
            await deleteMutation.mutateAsync(id);
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to delete');
        }
    };

    /* ---------------------------------------------------------------
     * Visibility summary — compact text showing where this template
     * is visible (used in the table cell for at-a-glance scanning).
     * --------------------------------------------------------------- */
    const visibilitySummary = (cl: ChecklistTemplate): string => {
        if (cl.isVisibleToAll) return 'All users';

        const parts: string[] = [];
        const companyCount = cl.companies?.length ?? 0;
        const userCount = cl.users?.length ?? 0;
        if (companyCount > 0) parts.push(`${companyCount} ${companyCount === 1 ? 'company' : 'companies'}`);
        if (userCount > 0) parts.push(`${userCount} ${userCount === 1 ? 'user' : 'users'}`);
        return parts.length ? parts.join(', ') : 'No one';
    };

    /* ---------------------------------------------------------------
     * Render
     * --------------------------------------------------------------- */
    /* Copy for whichever kind is showing — drives the create button and the
       empty state so both name the thing the author is actually making. */
    const copy = TYPE_COPY[type];

    return (
        <div className="checklists-tab" data-ref="organism-checklists-tab">
            {/* Kind sub-tabs — Work Order vs Inspection templates. Switching
                clears the search box: a title that matched in one kind rarely
                matches in the other, and an empty list under a stale search
                reads as "there are none" rather than "your filter is on". */}
            <TabBar
                className="checklists-tab__types"
                tabs={TYPE_TABS}
                activeKey={type}
                onChange={(key) => {
                    onTypeChange(key as ChecklistTemplateType);
                    onSearchChange('');
                }}
            />

            {/* Search + create row */}
            <div className="flex flex-col tablet:flex-row items-start tablet:items-center justify-between gap-12px mb-16px">
                <div className="w-full tablet:w-auto tablet:min-w-[280px]">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={`Search ${copy.noun}s by title...`}
                        className="w-full px-12px py-8px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                    />
                </div>
                <Button variant="primary" onClick={() => onCreate(type)}>{copy.createLabel}</Button>
            </div>

            {/* Error banner */}
            {error && (
                <Alert variant="error" className="mb-16px">{error}</Alert>
            )}

            {/* Loading state */}
            {isLoading && (
                <div className="px-16px py-24px text-center text-14px text-brand-text-muted">
                    Loading {copy.noun}s...
                </div>
            )}

            {/* Empty state — distinguishes "nothing here yet" from "nothing
                matched your search", which are different problems to fix. */}
            {!isLoading && checklists.length === 0 && !error && (
                <div className="px-16px py-32px text-center text-14px text-brand-text-muted border border-dashed border-brand-border rounded-md">
                    {trimmedSearch
                        ? `No ${copy.noun}s match "${trimmedSearch}".`
                        : `No ${copy.noun}s yet. Click "${copy.createLabel}" to create one.`}
                </div>
            )}

            {/* Data table */}
            {!isLoading && checklists.length > 0 && (
                <div className="overflow-x-auto border border-brand-border rounded-md">
                    <table className="w-full text-14px">
                        <thead className="bg-brand-surface text-brand-text-muted text-12px uppercase tracking-wide">
                            <tr>
                                <th className="text-left px-16px py-12px font-medium">Title</th>
                                <th className="text-left px-16px py-12px font-medium">Items</th>
                                <th className="hidden tablet:table-cell text-left px-16px py-12px font-medium">Version</th>
                                <th className="text-left px-16px py-12px font-medium">Visibility</th>
                                <th className="hidden tablet:table-cell text-left px-16px py-12px font-medium">In Use</th>
                                <th className="hidden tablet:table-cell text-left px-16px py-12px font-medium">Updated</th>
                                <th className="text-right px-16px py-12px font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {checklists.map((cl) => (
                                <tr key={cl.id} className="border-t border-brand-border hover:bg-brand-surface/40">
                                    <td className="px-16px py-12px">
                                        <div className="font-medium text-brand-text">{cl.title}</div>
                                        {cl.description && (
                                            <div className="text-12px text-brand-text-muted mt-2px line-clamp-1">
                                                {cl.description}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-16px py-12px text-brand-text">
                                        {cl.itemsCount ?? cl.items?.length ?? 0}
                                    </td>
                                    <td className="hidden tablet:table-cell px-16px py-12px text-brand-text">v{cl.version}</td>
                                    <td className="px-16px py-12px text-brand-text-muted">
                                        {visibilitySummary(cl)}
                                    </td>
                                    <td className="hidden tablet:table-cell px-16px py-12px text-brand-text-muted">
                                        {cl.instancesCount ?? 0}
                                    </td>
                                    <td className="hidden tablet:table-cell px-16px py-12px text-brand-text-muted">
                                        {new Date(cl.updatedAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-16px py-12px text-right">
                                        <div className="checklists-tab__row-actions inline-flex flex-wrap items-center justify-end gap-4px">
                                            <Button variant="ghost" size="sm" onClick={() => onEdit(cl.id)}>Edit</Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleDuplicate(cl.id)}>Duplicate</Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(cl.id, cl.title)}>Delete</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ChecklistsTab;
